import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AssistantIACard() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [tier, setTier] = useState("base");
  const [status, setStatus] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId] = useState(() => `host-${Math.random().toString(36).slice(2)}`);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/subscriptions/status").then(r => {
      setTier(r.data.tier || "base");
      setStatus(r.data.status || "");
    }).catch(() => {});
  }, []);

  const isEnterprise = tier === "enterprise" && (status === "active" || status === "trialing");

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages(m => [...m, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/ai/host-assistant", { session_id: sessionId, message: msg, lang });
      setMessages(m => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || (IT ? "Errore assistente" : "Assistant error");
      setMessages(m => [...m, { role: "assistant", text: detail, error: true }]);
    }
    finally { setSending(false); }
  };

  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="assistant-ia-card">
      <div className="flex items-center justify-between mb-3">
        <span className="uppercase tracking-[0.2em] text-[10px] text-muted-foreground">✨ Hospitality Assistant IA</span>
        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${isEnterprise ? "bg-sage text-white" : "bg-muted text-muted-foreground"}`} data-testid="assistant-ia-tier-badge">
          {isEnterprise ? "Enterprise" : (IT ? "Bloccato" : "Locked")}
        </span>
      </div>
      {!isEnterprise ? (
        <div className="text-center py-4" data-testid="assistant-ia-lock">
          <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-sand flex items-center justify-center text-xl">🔒</div>
          <p className="text-sm mb-2">
            {IT ? "Consulenza strategica IA riservata al piano Enterprise" : "AI strategic advisor reserved for Enterprise plan"}
          </p>
          <p className="text-[11px] text-muted-foreground mb-4">
            {IT ? "Chiedi consigli su tariffe, ospiti, marketing, compliance. Modello Claude Sonnet, contestualizzato sulla tua struttura." : "Ask about pricing, guests, marketing, compliance. Claude Sonnet, contextualized on your property."}
          </p>
          <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="assistant-ia-upgrade-btn">
            <Link to="/pricing">{IT ? "Passa a Enterprise €199/mese" : "Upgrade to Enterprise €199/mo"}</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="assistant-ia-chat">
          <div className="border border-border rounded p-3 bg-background max-h-64 overflow-y-auto space-y-2" data-testid="assistant-ia-messages">
            {messages.length === 0 ? (
              <p className="text-[11px] text-muted-foreground italic">
                {IT ? "Chiedi qualsiasi cosa sulla gestione della tua struttura." : "Ask anything about your property management."}
              </p>
            ) : messages.map((m, i) => (
              <div key={i} className={`text-xs ${m.role === "user" ? "text-right" : ""}`}>
                <span className={`inline-block px-2 py-1 rounded ${m.role === "user" ? "bg-primary/10" : m.error ? "bg-primary/20 text-primary" : "bg-sand"}`}>{m.text}</span>
              </div>
            ))}
            {sending && <p className="text-[11px] text-muted-foreground italic">{IT ? "Sto pensando…" : "Thinking…"}</p>}
          </div>
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={IT ? "Es. Come aumentare le prenotazioni ad agosto?" : "e.g. How to increase August bookings?"}
              className="text-sm" data-testid="assistant-ia-input"/>
            <Button size="sm" onClick={send} disabled={sending || !input.trim()} data-testid="assistant-ia-send">
              {sending ? "…" : (IT ? "Invia" : "Send")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
